import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { GoogleAdsRequestGq } from '../../../../../graphql/projects/models/GoogleAdsRequestGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { KeywordRequestTypes } from './DataCollectionModels';
import VoxelMenuItemContainer from '../../../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelAdvancedTextSearch from '../../../../voxel/Search/VoxelAdvancedTextSearch';
import HeadCell from '../../Shared/HeadCell';
import { KeywordTableColumn } from '../KeywordTableColumnPicker/models';
import { KeywordTableState } from '../logic/useKeywordTableState';
import HeadCellApiCollectionSelection from './HeadCellApiCollectionSelection';
import { HeadCellImplementationProps } from './models';
import HeadCellStandardOptions from '../../Shared/HeadCellStandardOptions';

export interface HeadCellRequestProps extends HeadCellImplementationProps {
  keywordTableState: KeywordTableState;
  column: KeywordTableColumn;
  requests?: Record<number, GoogleAdsRequestGq>;
  project: ProjectGq;
}

export interface Option {
  apiCallCollection: GoogleAdsRequestGq;
  primaryText: string;
  secondaryText: string;
}

const HeadCellRequest: React.FC<HeadCellRequestProps> = ({
  keywordTableState,
  requests,
  column,
  project,
  basicConfig,
  basicState,
}) => {
  // Keyword table state
  const { filterState, setFilterState } = keywordTableState;

  // Filter
  const selectedIds = keywordTableState.filterState.apiCallCollectionIds;
  const options = useMemo(
    () =>
      Object.values(requests ?? {})
        .filter(
          (acc) =>
            !acc.duplicateCheck &&
            (acc.parsingStatus === 'SUCCEEDED' || acc.parsingStatus === 'UNKNOWN')
        )
        .map((acc): Option => {
          let callType =
            KeywordRequestTypes.find((x) => x.type === acc.googleAdsRequestType)?.name ?? '';
          if (!!acc.comboId) callType = 'Combo search';
          return {
            apiCallCollection: acc,
            primaryText: !acc.name?.length
              ? '-'
              : acc.name.split(',').length > 1
                ? acc.name.split(',')[0] + '...'
                : acc.name,
            secondaryText: `${callType}, ${moment
              .utc(acc.createdTime)
              .local()
              .format('yyyy-MM-DD hh:mm')} (${moment.utc(acc.createdTime).local().fromNow()})`,
          };
        })
        .sort((a, b) => {
          return (
            Date.parse(b.apiCallCollection.createdTime) -
            Date.parse(a.apiCallCollection.createdTime)
          );
        }),
    [requests]
  );

  const handleFilterChange = useCallback(
    (id: number, checked: boolean) =>
      setFilterState((prev) => {
        const apiCallCollectionIds = [...prev.apiCallCollectionIds.filter((acc) => acc !== id)];
        checked && apiCallCollectionIds.push(id);
        return {
          ...prev,
          apiCallCollectionIds,
        };
      }),
    []
  );

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const displayedOptions = useMemo(() => {
    if (!searchTerm.length) {
      return options;
    }
    const words = searchTerm.split(' ').map((word) => word.toLowerCase());
    return options.filter((option) =>
      words.every(
        (word) =>
          option.primaryText.toLowerCase().includes(word) ||
          option.secondaryText.toLocaleLowerCase().includes(word)
      )
    );
  }, [searchTerm, options]);

  return (
    <HeadCell
      hasFilter={!!selectedIds.length}
      align={'left'}
      basicConfig={basicConfig}
      basicState={basicState}
    >
      <VoxelMenuItemContainer size="md">
        <HeadCellStandardOptions basicConfig={basicConfig} basicState={basicState} />
        <div className="p-2">
          <VoxelAdvancedTextSearch
            size="sm"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        {displayedOptions.map((option) => (
          <HeadCellApiCollectionSelection
            key={option.apiCallCollection.id}
            option={option}
            handleFilterChange={handleFilterChange}
            selectedIds={selectedIds}
          />
        ))}
      </VoxelMenuItemContainer>
    </HeadCell>
  );
};

export default HeadCellRequest;
